import { useEffect, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
} from '@heroicons/react/24/outline'
import { Card, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import apiClient from '@/api/client'

interface DocumentCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  documents_count: number
}

export default function AdminDocumentsPage() {
  const [categories, setCategories] = useState<DocumentCategory[]>([])
  const [loading, setLoading] = useState(true)

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<DocumentCategory | null>(null)
  const [form, setForm] = useState({ name: '', description: '', icon: 'folder' })
  const [saving, setSaving] = useState(false)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<DocumentCategory | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/document-categories/')
      setCategories(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenCreate = () => {
    setEditingCategory(null)
    setForm({ name: '', description: '', icon: 'folder' })
    setShowModal(true)
  }

  const handleOpenEdit = (category: DocumentCategory) => {
    setEditingCategory(category)
    setForm({
      name: category.name,
      description: category.description,
      icon: category.icon,
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (editingCategory) {
        await apiClient.patch(`/admin/document-categories/${editingCategory.id}/`, form)
      } else {
        await apiClient.post('/admin/document-categories/', form)
      }
      setShowModal(false)
      fetchCategories()
    } catch (error) {
      console.error('Error saving category:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!categoryToDelete) return
    setDeleting(true)
    try {
      await apiClient.delete(`/admin/document-categories/${categoryToDelete.id}/`)
      setShowDeleteModal(false)
      setCategoryToDelete(null)
      fetchCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento categorie..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Documenti</h1>
          <p className="text-gray-600">Gestisci le categorie dei documenti</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova Categoria
        </Button>
      </div>

      {/* Categories Grid */}
      {categories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <Card key={category.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <FolderIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{category.name}</h3>
                    <p className="text-sm text-gray-500">{category.documents_count} documenti</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleOpenEdit(category)}
                    className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setCategoryToDelete(category); setShowDeleteModal(true); }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {category.description && (
                <p className="mt-2 text-sm text-gray-600">{category.description}</p>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<FolderIcon className="h-12 w-12" />}
            title="Nessuna categoria"
            description="Crea la prima categoria per i documenti"
            action={{
              label: 'Nuova Categoria',
              onClick: handleOpenCreate
            }}
          />
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icona</label>
              <select
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="folder">Cartella</option>
                <option value="document">Documento</option>
                <option value="book-open">Libro</option>
                <option value="scale">Bilancia</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModal(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Categoria">
        <ModalBody>
          {categoryToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare la categoria{' '}
                <span className="font-semibold">{categoryToDelete.name}</span>?
              </p>
              {categoryToDelete.documents_count > 0 && (
                <p className="text-sm text-amber-600">
                  Attenzione: questa categoria contiene {categoryToDelete.documents_count} documenti.
                </p>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
